/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';

import classnames from 'classnames';
import { useLinkStyles } from '../../components/common/useLinkStyles';
import { useConfigurableFooter } from './useConfigurableFooter';
import SocialIcon from './SocialIcon';

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    color: theme.palette.footerText,
  },
  footer: {
    padding: '4rem',
    position: 'relative',
    background: theme.palette.footerBackground,
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '340px',

    [theme.breakpoints.down('sm')]: {
      padding: '1rem',
    },
  },
  footerColumnStyle: {
    display: 'flex',
    flexDirection: 'column',
  },
  iconFooterColumnStyle: {
    display: 'block',
  },
  icon: {
    height: '10rem',
  },
  footerSection: {
    color: theme.palette.footerText,
    [theme.breakpoints.down('sm')]: {
      marginRight: '1rem',
    },

    '& h2': {
      fontSize: '1.2rem',
      marginBottom: theme.spacing(2.5),

      [theme.breakpoints.down('sm')]: {
        fontSize: '0.8rem',
      },
    },

    '& a': {
      fontSize: '1rem',
      marginBottom: theme.spacing(2),
      color: theme.palette.footerText,
      [theme.breakpoints.down('sm')]: {
        fontSize: '0.5rem',
      },
    },

    '& a:hover': {
      color: `${theme.palette.footerText} !important`,
    },
  },
  footerVersionSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    color: theme.palette.footerText,
  },
  footerVersion: {
    marginTop: 'auto',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.5rem',
    },
  },
}));
export default function ConfigurableFooter({ className }) {
  const classes = useStyles();
  const linkClass = useLinkStyles();

  const { parsedFooterConfiguration } = useConfigurableFooter();
  const { t } = useTranslation();
  return (
    <div data-testid="configurable-footer" className={`${classes.footer} ${className}`}>
      {parsedFooterConfiguration?.map(configuration => (
        <div
          key={configuration.label}
          className={classnames(
            classes.footerSection,
            configuration.layout === 'icon' ? classes.iconFooterColumnStyle : classes.footerColumnStyle,
          )}
        >
          <h2 className={classes.sectionTitle}>{t(configuration.label)}</h2>

          {configuration.links?.map((link) => {
            if (link.icon && link.href) {
              return (
                <a key={link.href} target="_blank" rel="noreferrer" href={link.href} title={link.label ?? link.icon}>
                  <SocialIcon iconName={link.icon} color="secondary" sx={classes.icon} />
                </a>
              );
            }
            if (link.href) {
              return (
                <a key={link.href} href={link.href} target="_blank" rel="noreferrer" className={linkClass.link}>
                  {link.label ? t(link.label) : link.href}
                </a>
              );
            }
            if (link?.i18nHrefKey) {
              return (
                <a
                  key={t(link.i18nHrefKey)}
                  href={t(link.i18nHrefKey)}
                  target="_blank"
                  rel="noreferrer"
                  className={linkClass.link}
                >
                  {link.label ? t(link.label) : t(link.i18nHrefKey)}
                </a>
              );
            }

            return (
              <p key={link.label}>
                <Trans i18nKey={link.label} />
              </p>
            );
          })}
        </div>
      ))}

      <div className={classes.footerVersionSection}>
        <span className={classes.footerVersion}>{t('footer.version')}</span>
      </div>
    </div>
  );
}
